import { create } from 'zustand'
import {
  AccommodationEvent,
  CreateAccommodationEventsRequest,
  getAccommodationEvents,
  createAccommodationEvents,
  updateAccommodationEvent,
  deleteAccommodationEvent,
} from '../services/accommodationEvents'
import { devtools, persist } from 'zustand/middleware'

interface AccommodationEventsState {
  accommodationEvents: AccommodationEvent[]
  loading: boolean
  error: string | null
  isCreating: boolean
  isUpdating: boolean

  // Actions
  fetchAccommodationEvents: (
    studentId: string,
    classId: string,
    date?: Date,
  ) => Promise<void>
  saveAccommodationEvents: (
    data: CreateAccommodationEventsRequest,
  ) => Promise<AccommodationEvent[]>
  updateAccommodationEventStatus: (
    id: string,
    status: string,
  ) => Promise<AccommodationEvent>
  removeAccommodationEvent: (id: string) => Promise<void>
  clearError: () => void
}

export const useAccommodationEventsStore = create<AccommodationEventsState>()(
  devtools(
    persist(
      (set, get) => ({
        accommodationEvents: [],
        loading: false,
        error: null,
        isCreating: false,
        isUpdating: false,

        fetchAccommodationEvents: async (
          studentId: string,
          classId: string,
          date?: Date,
        ) => {
          set({ loading: true, error: null })
          try {
            const fetchedEvents = await getAccommodationEvents(
              studentId,
              classId,
              date,
            )

            // Instead of replacing all events, we should merge with existing ones
            // or in this case, since we're fetching specific events, just set them
            set({ accommodationEvents: fetchedEvents, loading: false })
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error ||
              error.message ||
              'Failed to fetch accommodation events'
            set({ error: errorMessage, loading: false })
            throw new Error(errorMessage)
          }
        },

        saveAccommodationEvents: async (
          data: CreateAccommodationEventsRequest,
        ) => {
          set({ isCreating: true, error: null })
          try {
            const newEvents = await createAccommodationEvents(data)

            // Update the store with the new events
            const { accommodationEvents } = get()
            const updatedEvents = [...accommodationEvents]

            // Replace or add each new event
            newEvents.forEach((newEvent) => {
              const existingIndex = updatedEvents.findIndex(
                (event) =>
                  event.student._id === newEvent.student._id &&
                  event.accommodation === newEvent.accommodation &&
                  new Date(event.date).toDateString() ===
                    new Date(newEvent.date).toDateString(),
              )

              if (existingIndex >= 0) {
                updatedEvents[existingIndex] = newEvent
              } else {
                updatedEvents.push(newEvent)
              }
            })

            set({ accommodationEvents: updatedEvents, isCreating: false })
            return newEvents
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error ||
              error.message ||
              'Failed to save accommodation events'
            set({ error: errorMessage, isCreating: false })
            throw new Error(errorMessage)
          }
        },

        updateAccommodationEventStatus: async (id: string, status: string) => {
          set({ isUpdating: true, error: null })
          try {
            const updatedEvent = await updateAccommodationEvent(id, { status })

            // Update the store
            const { accommodationEvents } = get()
            const updatedEvents = accommodationEvents.map((event) =>
              event._id === id ? updatedEvent : event,
            )

            set({ accommodationEvents: updatedEvents, isUpdating: false })
            return updatedEvent
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error ||
              error.message ||
              'Failed to update accommodation event'
            set({ error: errorMessage, isUpdating: false })
            throw new Error(errorMessage)
          }
        },

        removeAccommodationEvent: async (id: string) => {
          set({ error: null })
          try {
            await deleteAccommodationEvent(id)

            // Remove from store
            const { accommodationEvents } = get()
            const updatedEvents = accommodationEvents.filter(
              (event) => event._id !== id,
            )

            set({ accommodationEvents: updatedEvents })
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error ||
              error.message ||
              'Failed to delete accommodation event'
            set({ error: errorMessage })
            throw new Error(errorMessage)
          }
        },

        clearError: () => set({ error: null }),
      }),
      {
        name: 'accommodation-events-store',
      },
    ),
    {
      name: 'accommodation-events-store-devtools',
    },
  ),
)
